import "./Icon.scss";
import React, { useState, useRef } from "react";
import { useSelector } from "react-redux";
import { CgPlayButtonR } from "react-icons/cg";
import { COLOR_BLUE } from "../../constants";
import ToolTip from "../ToolTip/ToolTip";
import { authSelector } from "../../redux/selectors/auth.selector";
import { fetchProgramVideoPlayUrl } from "../../services/api.service";

interface PlayProps {
  program_item_id?: number;
  video_preparing?: boolean;
  video_file_name?: string;
}

function Play(props: PlayProps) {
  const [isVideoVisible, setVideoVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [playUrl, setPlayUrl] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const authState = useSelector(authSelector);

  // 動画ファイルがあるかどうかを判定
  const hasVideo = props.program_item_id && props.video_file_name && !props.video_preparing;
  

  const fetchVideoPlayUrl = React.useCallback(async () => {
    const currentProgramItemId = props.program_item_id;
    
    if (!currentProgramItemId || isLoading) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetchProgramVideoPlayUrl(currentProgramItemId, authState);
      setPlayUrl(response.data.play_url);
      return response.data.play_url;
    } catch (error) {
      alert("動画の読み込みに失敗しました。");
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [props.program_item_id, isLoading, authState]);


  const handlePlayClick = React.useCallback(async (event: React.MouseEvent) => {
    event.stopPropagation();
    
    const currentProgramItemId = props.program_item_id;
    
    // S3利用料低減：再生ボタン押下時のみ署名付きURLを生成
    const url = playUrl || (currentProgramItemId ? await fetchVideoPlayUrl() : null);
    
    if (url && videoRef.current) {
      
      // 動画の読み込み完了を待ってから再生・フルスクリーン処理を実行
      const playVideo = async () => {
        try {
          // まず動画を表示状態にする
          setVideoVisible(true);
          
          // 少し待ってからsrcを設定（DOM更新を待つ）
          await new Promise(resolve => setTimeout(resolve, 100));
          videoRef.current!.src = url;
          
          // 動画の読み込み完了を待つ
          await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => {
              videoRef.current!.removeEventListener("canplay", handleCanPlay);
              videoRef.current!.removeEventListener("loadeddata", handleCanPlay);
              videoRef.current!.removeEventListener("error", handleError);
              reject(new Error("動画の読み込みがタイムアウトしました"));
            }, 30000); // 30秒タイムアウト
            
            const handleCanPlay = () => {
              clearTimeout(timeout);
              videoRef.current!.removeEventListener("canplay", handleCanPlay);
              videoRef.current!.removeEventListener("loadeddata", handleCanPlay);
              videoRef.current!.removeEventListener("error", handleError);
              resolve();
            };
            
            const handleError = () => {
              clearTimeout(timeout);
              videoRef.current!.removeEventListener("canplay", handleCanPlay);
              videoRef.current!.removeEventListener("loadeddata", handleCanPlay);
              videoRef.current!.removeEventListener("error", handleError);
              reject(new Error("動画の読み込みに失敗しました"));
            };
            
            videoRef.current!.addEventListener("canplay", handleCanPlay);
            videoRef.current!.addEventListener("loadeddata", handleCanPlay);
            videoRef.current!.addEventListener("error", handleError);
            
            // 動画の読み込みを開始
            videoRef.current!.load();
            
            // すでに読み込み済みの場合
            if (videoRef.current!.readyState >= 3) {
              handleCanPlay();
            }
          });
          
          // 動画を再生
          await videoRef.current!.play();
          
          // フルスクリーン化
          try {
            await videoRef.current!.requestFullscreen();
          } catch (fullscreenError) {
            // フルスクリーン化に失敗しても動画再生は続行
          }
          
        } catch (error) {
          alert("動画の再生に失敗しました。");
          setVideoVisible(false);
        }
      };
      
      // フルスクリーン終了時のイベントリスナー
      const handleFullscreenChange = () => {
        if (!document.fullscreenElement) {
          // フルスクリーン終了時は動画を停止して非表示にする
          setVideoVisible(false);
          if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.src = "";
          }
          cleanup();
        }
      };
      
      // クリーンアップ用のイベントリスナー削除
      const cleanup = () => {
        document.removeEventListener("fullscreenchange", handleFullscreenChange);
      };

      // イベントリスナーを追加
      document.addEventListener("fullscreenchange", handleFullscreenChange);
      
      playVideo();
    }
  }, [props.program_item_id, playUrl, fetchVideoPlayUrl]);

  // webkit系のフルスクリーン終了イベント（モバイル対応）
  React.useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const handleWebkitEndFullscreen = () => {
      setVideoVisible(false);
      videoElement.pause();
      videoElement.src = "";
    };

    videoElement.addEventListener("webkitendfullscreen", handleWebkitEndFullscreen);

    return () => {
      videoElement.removeEventListener("webkitendfullscreen", handleWebkitEndFullscreen);
    };
  }, [isVideoVisible]);

  return (
    <>
      {hasVideo ? (
        <>
          <div>
            {props.video_preparing || isLoading ? (
              <ToolTip text={isLoading ? "読み込み中..." : "準備中"} content={
                <CgPlayButtonR
                  color="gray"
                  size={36}
                  style={{ marginLeft: 10, marginRight: 10 }}
                  onClick={(event) => {
                    event.stopPropagation();
                  }} />
              } />
            ) : (
              <CgPlayButtonR
                className="icon hover_blue"
                color={COLOR_BLUE}
                size={36}
                onClick={handlePlayClick}
              />
            )}
          </div>
          <video
            id="video"
            ref={videoRef}
            controls
            preload="none"
            style={{
              position: isVideoVisible ? "fixed" : "absolute",
              top: isVideoVisible ? "0" : "-1px",
              left: isVideoVisible ? "0" : "-1px", 
              width: isVideoVisible ? "100vw" : "1px",
              height: isVideoVisible ? "100vh" : "1px",
              zIndex: isVideoVisible ? 9999 : -1,
              visibility: isVideoVisible ? "visible" : "hidden",
              opacity: isVideoVisible ? 1 : 0,
              pointerEvents: isVideoVisible ? "auto" : "none"
            }}
          />
        </>
      ) : null}
    </>
  );
};

export default Play;
